import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PropTypes } from 'prop-types';
import { useDataService } from './hooks/useDataService';
import { useOptionMapper } from './hooks/useOptionMapper';
import { useSelectionManager } from './hooks/useSelectionManager';
import { Header } from './components/Header';
import { SelectComponent } from './components/SelectComponent';
import { Pagination } from '../../../core/components/Pagination';
import { selectAll } from '../../../utils';

export const AsyncSelectFilter = props => {
  const {
    className,
    dataObjectType,
    dataObjectAttribute,
    dataObjectAction,
    selectedValues: propSelectedValues,
    valueOverride: propValueOverride,
    filters: propFilters,
    onChange,
    onRadioChange,
    singleSelect,
    pageSize,
  } = props;

  const [activePage, setActivePage] = useState(1);

  const optionMapper = useOptionMapper(props);
  const selectionManager = useSelectionManager(props);

  const { valueList, totalCount, isLoading, inputValue, setInputValue, getData, debouncedGetData } =
    useDataService(props);

  // Map selected values to options
  const selectedOptions = useMemo(() => {
    return optionMapper.mapSelectedValuesToOptions(propSelectedValues);
  }, [propSelectedValues, optionMapper]);

  // Initial data load
  useEffect(() => {
    getData(propFilters, false, activePage, inputValue);
  }, []);

  const handleChange = useCallback(
    newSelectedOptions => {
      selectionManager.handleSelectionChange(newSelectedOptions, onChange, onRadioChange, valueList);
    },
    [selectionManager, onChange, onRadioChange]
  );

  const handleInputChange = useCallback(
    (newInputValue, { action }) => {
      if (action === 'input-change') {
        setInputValue(newInputValue);
        debouncedGetData(propFilters, true, 1, newInputValue);
        setActivePage(1);
      }
    },
    [debouncedGetData, propFilters, setInputValue]
  );

  const handlePageChange = useCallback(
    eventKey => {
      setActivePage(eventKey);
      getData(propFilters, false, eventKey, inputValue);
    },
    [getData, propFilters, inputValue]
  );

  const clearAll = useCallback(() => {
    handleChange(singleSelect ? null : []);
  }, [handleChange, singleSelect]);

  const handleSelectAll = useCallback(async () => {
    const { dataUrl, filters, dataEntity, sortBy, authToken } = props;

    try {
      const selectedIds = await selectAll({
        endpoint: dataUrl,
        filters,
        isChecked: true,
        totalCount,
        selectedIds: [],
        entity: dataEntity,
        sortValue: sortBy,
        authToken,
      });

      const newFormData = selectedIds.reduce((cache, value) => {
        cache[value] = cache[value] || {};
        cache[value].checked = true;
        return cache;
      }, {});
      selectedIds.map(id => ({ value: id }));

      handleChange(selectedIds.map(id => ({ value: id })));
    } catch (error) {
      console.error('Error selecting all:', error);
    }
  }, [props, totalCount, handleChange]);

  const itemCount = useCallback(() => {
    if (singleSelect) {
      return selectedOptions ? 1 : 0;
    }
    return Array.isArray(selectedOptions) ? selectedOptions.length : 0;
  }, [selectedOptions, singleSelect]);

  const noItemsChecked = useCallback(() => {
    return itemCount() === 0;
  }, [itemCount]);

  const renderPagination = useCallback(() => {
    return totalCount > pageSize ? (
      <div className="text-center m-t-10">
        <Pagination
          activePage={activePage}
          onPageChange={handlePageChange}
          pageSize={pageSize}
          totalCount={totalCount}
        />
      </div>
    ) : null;
  }, [totalCount, pageSize, activePage, handlePageChange]);

  return (
    <div className={`checkbox-list ${className}`}>
      <Header {...props} selectedCount={itemCount()} onSelectAll={handleSelectAll} onClearAll={clearAll} />

      <div
        data-object-type={dataObjectType}
        data-object-attribute={dataObjectAttribute}
        data-object-action={dataObjectAction}
      >
        <div className="select-wrapper">
          <div className="list-content">
            <SelectComponent
              {...props}
              selectedOptions={selectedOptions}
              valueList={valueList}
              isLoading={isLoading}
              onChange={handleChange}
              onInputChange={handleInputChange}
            />
          </div>
          {props.middlePanel}
        </div>
      </div>

      {renderPagination()}
      {!noItemsChecked() && !!props.renderClearFilters && props.renderClearFilters(clearAll)}
    </div>
  );
};

AsyncSelectFilter.propTypes = {
  className: PropTypes.string,
  heading: PropTypes.string,
  headingLevel: PropTypes.number,
  showSelectOptions: PropTypes.bool,
  disabled: PropTypes.bool,
  dataUrl: PropTypes.string,
  dataInclude: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
  dataObjectAction: PropTypes.string,
  dataObjectAttribute: PropTypes.string,
  dataObjectType: PropTypes.string,
  dataValue: PropTypes.string,
  authToken: PropTypes.string,
  valueKey: PropTypes.string.isRequired,
  labelKey: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onRadioChange: PropTypes.func,
  labelFunction: PropTypes.func,
  relationKey: PropTypes.string,
  filters: PropTypes.object,
  filterField: PropTypes.string,
  renderClearFilters: PropTypes.func,
  defaultRelationData: PropTypes.func,
  pageSize: PropTypes.number,
  queryFields: PropTypes.object,
  maxPageButtons: PropTypes.number,
  sortBy: PropTypes.string,
  dataEntity: PropTypes.string,
  searchWrapperClasses: PropTypes.string,
  singleSelect: PropTypes.bool,
  name: PropTypes.string,
  searchCaption: PropTypes.string,
  middlePanel: PropTypes.element,
  valueOverride: PropTypes.bool,
  readOnlyList: PropTypes.array,
  onDataLoaded: PropTypes.func,
  uniqueKey: PropTypes.string,
  selectedValues: PropTypes.oneOfType([PropTypes.array, PropTypes.any]),
  renderRow: PropTypes.func, // (checkboxElement, fieldGroupName, disabled, value, key, isSelected)
  selectProps: PropTypes.object,
};

AsyncSelectFilter.defaultProps = {
  headingLevel: 4,
  relationValueKeys: [],
  maxPageButtons: 5,
  pageSize: 10,
  showSelectOptions: true,
  searchChangeDelay: 1000,
  searchCaption: '',
  searchWrapperClasses: 'full-width m-b-5',
  selectProps: {},
  selectedValues: [],
};
